import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createFlightPlace,
  getAllFlightPlaces,
  getFlightPlaceByID,
  updateFlightPlace,
} from "./services/flightplace.services";
import { clearFlightPlaceDetails } from "./flightplaceSlice";
import { closed } from "../../components/drawer/drawerSlice";

const CreateFlightPlace: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const FlightPlaceDetail = useAppSelector(
    (state) => state.flightplace.FlightPlaceDetail
  );
  const FlightPlaceID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.flightplace.formLoading);

  useEffect(() => {
    return () => {
      dispatch(clearFlightPlaceDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (FlightPlaceID) {
      dispatch(getFlightPlaceByID(FlightPlaceID));
    }
  }, [FlightPlaceID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!FlightPlaceID) {
        const insertPayload = {
          FlightPlaceName: values.FlightPlaceName,
          FlightPlaceCode: values.FlightPlaceCode,
          IsActive: values.IsActive ? values.IsActive : true,
        };
        const response = await dispatch(createFlightPlace(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearFlightPlaceDetails());
          dispatch(getAllFlightPlaces());
        }
      } else {
        const editPayload = {
          ID: FlightPlaceID,
          FlightPlaceName: values.FlightPlaceName,
          FlightPlaceCode: values.FlightPlaceCode,
          IsActive: values.IsActive ? values.IsActive : false,
        };
        const response = await dispatch(updateFlightPlace(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearFlightPlaceDetails());
          dispatch(getAllFlightPlaces());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={FlightPlaceDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="FlightPlaceName"
                label="Flight Place"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="FlightPlaceCode"
                label="Flight Place Code"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={FlightPlaceID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateFlightPlace;
